import { Box, Checkbox, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import { useCallback, useMemo } from 'react';

export type BaseListBoxOption = {
  id: string;
};

export type ListBoxProps<Option extends BaseListBoxOption = BaseListBoxOption> = {
  name: string;
  options: Option[];
  /**
     * Used to determine the label for a given option.
    */
  getOptionLabel: (option: Option) => string;
  /**
     * Used to determine the disabled state for a given option.
     */
  getOptionDisabled?: (option: Option) => boolean;
  /**
     * Used to determine the variant to render the options as.
     */
  optionVariant?: 'checkbox' | 'default';


  color?: 'primary' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  disableListPadding?: boolean;
};

export const ListBox = <Option extends BaseListBoxOption>({ color = 'secondary', optionVariant = 'checkbox', ...props }: ListBoxProps<Option>) => {
  const [ field, _meta, { setValue, setTouched } ] = useField<string[]>(props.name);
  const { classes, cx } = useListBoxStyles();

  const selectedIds = useMemo(() => field.value, [ field.value ]);

  const onSelectValue = useCallback((value: BaseListBoxOption, select: boolean) => {
    if (select) {
      setValue([ ...selectedIds, value.id ], true);
    } else {
      setValue(selectedIds.filter(id => id !== value.id), true);
    }

    setTouched(true, false);
  }, [ selectedIds, setTouched, setValue ]);

  return (
    <Box className={classes.listBox}>
      <List component="ul" disablePadding={!props.options.length || props.disableListPadding} className={classes.list}>
        {props.options.map((option) => {
          const selected = selectedIds.includes(option.id);
          const label = props.getOptionLabel(option);
          const disabled = props.disabled || props.getOptionDisabled?.(option);

          return (
            <ListItemButton
              key={option.id}
              selected={selected}
              disableRipple
              component="li"
              aria-selected={selected}
              className={cx(classes.listItem, {
                [classes.checkboxListItem]: optionVariant === 'checkbox',
                [classes.listItemPrimary]: color === 'primary'
              })}
              onClick={() => onSelectValue(option, !selected)}
              disabled={disabled}
            >
              {optionVariant === 'checkbox' && (
                <Checkbox
                  color={color}
                  checked={selected}
                  disableRipple
                  disabled={disabled}
                />
              )}
              <ListItemText>{label}</ListItemText>
            </ListItemButton>
          );
        })}
      </List>
      {props.options.length === 0 && (
        <Box className={classes.emptyListContainer}>
          {!props.loading && (
            <Typography variant="body2" className={classes.emptyListText}>
              {props.emptyMessage}
            </Typography>
          )}
          {props.loading && <CircularProgress color={color} />}
        </Box>
      )}
    </Box>
  );
};

const useListBoxStyles = makeStyles({ name: 'ListBox' })(theme => ({
  listBox: {
    height: '100%',
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    '&::-webkit-scrollbar': {
      backgroundColor: theme.palette.grey[100],
      borderLeft: `1px solid ${theme.palette.divider}`,
      width: '14px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderWidth: '4px',
      borderRadius: '8px'
    }
  },
  list: {
    background: theme.palette.common.white,
  },
  listItem: {
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.secondary.background,
      '&:hover': {
        backgroundColor: theme.palette.secondary.background,
      }
    }
  },
  listItemPrimary: {
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.primary.background,
      '&:hover': {
        backgroundColor: theme.palette.primary.background,
      }
    }
  },
  checkboxListItem: {
    padding: 0
  },
  emptyListContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${theme.spacing(5)} 0`
  },
  emptyListText: {
    maxWidth: '410px',
    textAlign: 'center',
    color: theme.palette.text.hint
  }
}));
